import type { ReactNode } from "react";

type AxisValue = string | boolean | null;

type VariantMatrixProps<
  RowKey extends string,
  ColKey extends string,
  RowValue extends AxisValue,
  ColValue extends AxisValue,
> = {
  rowAxis: { name: RowKey; values: readonly RowValue[] };
  colAxis: { name: ColKey; values: readonly ColValue[] };
  render: (row: RowValue, col: ColValue) => ReactNode;
};

/**
 * 2軸の直積でバリアントを描画するグリッド。
 * @param {VariantMatrixProps} props 軸定義と render 関数
 * @return {JSX.Element} variant × variant の表
 */
export const VariantMatrix = <
  RowKey extends string,
  ColKey extends string,
  RowValue extends AxisValue,
  ColValue extends AxisValue,
>(
  props: VariantMatrixProps<RowKey, ColKey, RowValue, ColValue>,
): JSX.Element => {
  const { rowAxis, colAxis, render } = props;

  const labelOf = (v: AxisValue): string =>
    v === null ? "—" : typeof v === "boolean" ? String(v) : String(v);

  return (
    <div className="overflow-x-auto">
      <table className="table table-xs">
        <thead>
          <tr>
            <th className="text-base-content/50 text-xs font-normal">
              {rowAxis.name} ＼ {colAxis.name}
            </th>
            {colAxis.values.map(col => (
              <th
                key={labelOf(col)}
                className="text-base-content/70 text-xs font-medium"
              >
                {labelOf(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowAxis.values.map(row => (
            <tr key={labelOf(row)}>
              <td className="text-base-content/70 text-xs font-medium whitespace-nowrap">
                {labelOf(row)}
              </td>
              {colAxis.values.map(col => (
                <td
                  key={`${labelOf(row)}-${labelOf(col)}`}
                  className="align-middle"
                >
                  {render(row, col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
